import { Component } from 'preact';
import { scaleLinear } from 'd3-scale';
import { extent, sum } from 'd3-array';
import { select } from 'd3-selection';
import 'd3-transition';

import { convertWindBearing8 } from '../../utils/convert-wind-bearing';
import { getRainColourInterpolator, getWindColourInterpolator } from '../../utils/interpolators';
import RainDrop from '../rain-drop';
import Button from '../button';
import { formatRainfall, formatWindGust, getDateTimeFormatConverter } from '../../utils/formatters';
import style from './style.scss';
import { convertRainfall } from '../../utils/unit-converters';
import { RAIN_UNIT } from '../../utils/constants';

/**
 * The DailyList component displays a list of daily records, in a vertical arrangement.
 * It is used instead of the DailyChartWrapper component on smaller screens where there is
 * insufficient width to draw a chart.
 *
 * NOTE: data passed down is already converted into the units set by the user.
 *
 * props:
 * - data: An array of objects, each containing daily record data
 * - descending: optional - set to true to sort in descending order
 */

class DailyList extends Component {

	constructor(props, context) {
		super(props, context);

		this.units = context.userSettings.units;
		this.isExpandedByDefault = context.userSettings.ui.dailyListExpanded;

		this.getWindColour = getWindColourInterpolator(this.units.speed);
		this.getRainColour = getRainColourInterpolator(convertRainfall(50, this.units.rain));

		this.state = {
			isExpanded: this.isExpandedByDefault
		};

		// keep track of temperature bar transitions, so they don't repeat when list expanded/collapsed
		this.temperatureBarTransitions = {};

		this.formatters = {
			date:     getDateTimeFormatConverter('%Y-%m-%d', '%-d %b'),
			rainfall: r => formatRainfall(r, this.units.rain, true)
		};

		this.toggleNumItems = this.toggleNumItems.bind(this);
	}

	componentWillReceiveProps(nextProps) {
		// When the data changes, reset the transition states
		if (nextProps.data !== this.props.data) {
			this.temperatureBarTransitions = {};
		}
	}

	render(props) {
		if (!Array.isArray(props.data)) {
			return null;
		}

		const formattedData = this.formatData(props.data);

		if (props.descending) {
			formattedData.reverse(); // display most recent at the top
		}

		formattedData.forEach(d => {
			if (this.temperatureBarTransitions[d.date] === undefined) {
				this.temperatureBarTransitions[d.date] = false;
			}
		});

		// This scale will define the horizontal positions of the temperature bars
		this.tempScale = scaleLinear()
			.range([0, 100])
			.domain(extent([]
				.concat(formattedData.map(d => d.highTemperature))
				.concat(formattedData.map(d => d.lowTemperature))
			));

		return (
			<div>
				<ul class={style.daily_list}>
					<li class={style.daily_list_head} key="labels">
						<div class={style.daily_list_date_column}>Date</div>
						<div class={style.daily_list_temp_column}>Min/max (°{this.units.temperature})</div>
						<div class={style.daily_list_rain_column}>Rain ({this.units.rain})</div>
						<div class={style.daily_list_gust_column}>Gust ({this.units.speed})</div>
					</li>
					{
						formattedData.slice(0, this.isExpanded() ? formattedData.length : 12).map(ob => (
							<li class={style.daily_list_row} key={ob.key}>

								<div class={style.daily_list_date_column}>
									{ ob.date }
								</div>

								<div class={style.daily_list_temp_column}>
									{ ob.empty ? '---' : this.renderTemperature(ob) }
								</div>

								<div class={style.daily_list_rain_column}>
									{ ob.empty ? '---' : this.renderRain(ob) }
								</div>

								<div class={style.daily_list_gust_column}>
									{ ob.empty ? '---' : this.renderWind(ob) }
								</div>

							</li>
						))
					}
				</ul>
				{
					this.isExpanded()
						? <p class={style.daily_list_note}>Rain Total = { this.formatters.rainfall(this.getRainTotal()) + ' ' + this.units.rain }</p>
						: null
				}
				{ this.renderToggleButton() }
			</div>
		);
	}

	renderToggleButton() {
		if (this.props.alwaysExpanded) {
			return null;
		}

		return (
			<Button wide clickHandler={this.toggleNumItems}>
				{ this.isExpanded() ? 'Show less' : 'Show more' }
			</Button>
		);
	}

	renderRain(ob) {
		return (
			<span class={style.daily_list_rain_cell}>
				{
					ob.rainfall === null
						? 'n/a'
						: (ob.rainfall > 0 ? this.formatters.rainfall(ob.rainfall) : '.')
				}
				<span class={style.daily_list_raindrop}>
					{
						(ob.rainfall !== null && ob.rainfall > 0)
							? <RainDrop fill={this.getRainColour(ob.rainfall, this.units.rain)}
							            size={this.getRainDropSize(ob.rainfall)} />
							: null
					}
				</span>
			</span>
		);
	}

	getRainTotal() {
		return sum(this.props.data
			.filter(d => !d.empty && d.rainfall !== null)
			.map(d => d.rainfall)
		);
	}

	renderWind(ob) {
		if (ob.highWindGust !== null) {
			return (
				<span class={style.daily_list_wind_gust_cell}>
					<span class={style.daily_list_wind_gust_dir}>{ ob.highWindGustDirection }</span>
					<span class={style.daily_list_wind_gust_value}>
						<span class={style.daily_list_wind_cell}
						      style={{ backgroundColor: this.getWindColour(ob.highWindGust, this.units.speed) }}>
							{ formatWindGust(ob.highWindGust, this.units.speed, true, true) }
						</span>
					</span>
				</span>
			);
		}
		return 'n/a';
	}

	renderTemperature(ob) {
		const avgX = ob.avgTemperature === null
			? 0.5 * sum(this.tempScale.range())
			: this.tempScale(ob.avgTemperature);

		const lowBarLeft = ob.lowTemperature === null
			? avgX
			: this.tempScale(ob.lowTemperature);

		const lowBarWidth = (ob.lowTemperature === null || ob.avgTemperature === null)
			? 0
			: avgX - lowBarLeft;

		const highBarLeft = avgX;

		const highBarWidth = (ob.highTemperature === null || ob.avgTemperature === null)
			? 0
			: this.tempScale(ob.highTemperature) - highBarLeft;

		return (
			<div class={style.daily_list_temp_cell}>

				<span class={style.daily_list_temp_label_low}
				      style={{ right: `${100 - lowBarLeft}%` }}>
					{
						ob.lowTemperature === null ? 'n/a' : ob.lowTemperature
					}
				</span>

				<span class={style.daily_list_temp_bar_low}
				      style={{ right: `${100 - highBarLeft}%` }}
				      ref={el => el && this.transitionWidth(el, ob.date, lowBarWidth)} />

				<span class={style.daily_list_temp_bar_high}
				      style={{ left: `${highBarLeft}%` }}
				      ref={el => el && this.transitionWidth(el, ob.date, highBarWidth)} />

				<span class={style.daily_list_temp_label_high}
				      style={{ left: `${highBarLeft + highBarWidth}%` }}>
					{
						ob.highTemperature === null ? 'n/a' : ob.highTemperature
					}
				</span>

			</div>
		);
	}

	transitionWidth(el, key, width) {
		const d3Elem = select(el);

		if (this.temperatureBarTransitions[key] === false) {
			// Transition has not been run for this date yet within this component instance,
			// so animate the width.
			d3Elem.transition()
				.duration(500)
				.styleTween('width', () => t => `${t * width}%`)
				.on('end', () => { this.temperatureBarTransitions[key] = true; });
		} else {
			// Transition has already been run. Set the width immediately, don't animate it.
			d3Elem.style('width', `${width}%`);
		}
	}

	isExpanded() {
		return this.props.alwaysExpanded || this.state.isExpanded;
	}

	toggleNumItems() {
		this.setState(state => ({ isExpanded: !state.isExpanded }));
	}

	getRainDropSize(rain) {
		const rainMm = this.units.rain === RAIN_UNIT.IN ? rain * 25.4 : rain;
		return Math.min(32, 10 + 0.5 * rainMm);
	}

	formatData(data) {
		return data.map((ob, index) => {

			const date = this.formatters.date(ob.date);

			const highWindGustDirection = ob.highWindGustBearing === null
				? null
				: (ob.highWindGust > 0 ? convertWindBearing8(ob.highWindGustBearing) : '-');

			return {
				...ob,
				date,
				highWindGustDirection,
				key: index
			};
		});
	}

}

export default DailyList;



// WEBPACK FOOTER //
// ./components/daily-list/index.js